import React, { useEffect, memo } from "react";

interface ModalProps {
 isOpen: boolean;
 onClose: () => void;
 title?: string;
 children: React.ReactNode;
 size?: "sm" | "md" | "lg" | "xl" | "fullscreen";
 footer?: React.ReactNode;
 fixedTotal?: React.ReactNode;
 headerActions?: React.ReactNode;
 headerContent?: React.ReactNode;
 tabs?: React.ReactNode; // Tabs component to render between header and content
 errors?: React.ReactNode; // Error messages to display in fixed position (not scrolling)
}

const ModalComponent: React.FC<ModalProps> = ({
 isOpen,
 onClose,
 title,
 children,
 size = "md",
 footer,
 fixedTotal,
 headerActions,
 headerContent,
 tabs,
 errors,
}) => {
 // Handle Escape key
 useEffect(() => {
  if (!isOpen) return;

  const handleEscape = (e: KeyboardEvent) => {
   if (e.key === "Escape") {
    onClose();
   }
  };

  document.addEventListener("keydown", handleEscape);
  return () => {
   document.removeEventListener("keydown", handleEscape);
  };
 }, [isOpen, onClose]);

 // Manage body overflow
 useEffect(() => {
  if (isOpen) {
   document.body.style.overflow = "hidden";
  } else {
   document.body.style.overflow = "";
  }

  return () => {
   document.body.style.overflow = "";
  };
 }, [isOpen]);

 if (!isOpen) return null;

 const sizeClasses = {
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-4xl",
  fullscreen: "max-w-full h-screen",
 };

 const isFullscreen = size === "fullscreen";

 return (
  <div
   className="fixed inset-0 z-[100] overflow-y-auto"
   aria-labelledby={title ? "modal-title" : undefined}
   role="dialog"
   aria-modal="true"
  >
   {/* Background overlay */}
   <div
    className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
    onClick={(e) => {
      // Only close if clicking directly on the overlay, not on child elements
      if (e.target === e.currentTarget) {
        onClose();
      }
    }}
    onMouseDown={(e) => {
      // Prevent mousedown from bubbling
      if (e.target !== e.currentTarget) {
        e.stopPropagation();
      }
    }}
   ></div>

   {/* Modal panel */}
   <div className={`flex ${isFullscreen ? "min-h-screen" : "min-h-full items-center justify-center p-4"}`}>
    <div
     className={`relative bg-white transform transition-all w-full flex flex-col ${isFullscreen ? "h-screen" : `max-h-[90vh] ${sizeClasses[size]}`}`}
     onClick={(e) => e.stopPropagation()}
     onMouseDown={(e) => e.stopPropagation()}
     onMouseUp={(e) => e.stopPropagation()}
    >
     {/* Header */}
     <div className={`flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0 ${tabs ? "border-b-0" : ""}`}>
      {headerContent || (
       <>
        {title && (
         <h3 className="text-lg font-semibold text-gray-900" id="modal-title">
          {title}
         </h3>
        )}
        <div className="flex items-center gap-3 ml-auto">
         {headerActions}
         <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-store-secondary"
         >
          <span className="sr-only">Close</span>
          <svg
           className="h-6 w-6"
           fill="none"
           viewBox="0 0 24 24"
           stroke="currentColor"
          >
           <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
           />
          </svg>
         </button>
        </div>
       </>
      )}
     </div>

     {/* Tabs (if provided) - directly under header with no gap */}
     {tabs && (
      <div className="flex-shrink-0">
       {tabs}
      </div>
     )}

     {/* Fixed Errors Section (if provided) */}
     {errors && (
      <div className={`border-b border-red-200 bg-red-50 ${isFullscreen ? "px-4 py-3" : "px-6 py-4"} flex-shrink-0`}>
       {errors}
      </div>
     )}

     {/* Fixed Total Section (if provided) */}
     {fixedTotal && (
      <div className={`border-b border-gray-200 bg-gray-50 ${isFullscreen ? "px-4 py-3" : "px-6 py-4"} flex-shrink-0`}>
       {fixedTotal}
      </div>
     )}

     {/* Scrollable Content */}
     <div 
      className={`overflow-y-auto flex-1 ${isFullscreen ? "p-4" : "p-6"} ${footer ? "pb-0" : ""}`}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseUp={(e) => e.stopPropagation()}
     >
      {children}
     </div>

     {/* Fixed Footer (if provided) - outside scrollable content */}
     {footer && (
      <div className={`border-t border-gray-200 bg-white flex-shrink-0 ${isFullscreen ? "p-4" : "p-6"}`}>
       {footer}
      </div>
     )}
    </div>
   </div>
  </div>
 );
};

export const Modal = memo(ModalComponent);
